// @ts-nocheck
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, ThemeProvider, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';

import { FHConfirmDialog, FHMUIDataTable } from '../../../base';
import { claimsActions } from '../../../../store';
import { FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import { claimProcedureTableColDefs, sessionsInClaimTableOptions } from './config';
import { getSessionsInClaimsData } from '../claims/utils';
import { getUtcOffset } from '../../../../utils/common';
import { formatSecondsToHHMM, history } from '../../../../helpers';
import ErrorAlertIcon from '../../../svgs/ErrorAlertIcon';
import { correctClaimTableTheme } from '../../../../utils/theme';

import type { RootState } from '../../../../store';
import type { IClaim } from '../../../../utils/types';

function getPayorClaimIdErrorMessage(
  payor_claim_id: string | null,
  clearing_house_claim_id: string | null,
  secondary_payor_claim_id: string | null,
  secondary_status: string | null,
) {
  const errMsg = 'Please provide the following fields before generating an updated claim: ';
  const fields = [];
  if (!payor_claim_id) {
    fields.push('Payor claim ID');
  }
  if (!clearing_house_claim_id) {
    fields.push('Clearinghouse ID');
  }
  if (secondary_status && !secondary_payor_claim_id) {
    fields.push('Secondary payor claim id');
  }
  if (fields.length) {
    return (
      <Typography>
        <Typography>{errMsg}</Typography>
        {fields.map((field) => (
          <Typography key={field}>{`* ${field}`}</Typography>
        ))}
      </Typography>
    );
  } else {
    return '';
  }
  return errMsg;
}

interface ICorrectClaimDialogProps {
  show: boolean;
  onClose: () => void;
}

interface IContentRendererProps {
  claim: IClaim | null;
}

function CorrectClaimContent({ claim }: IContentRendererProps) {
  const totalDuration = useMemo(() => {
    if (!claim || claim?.procedures?.length === 0) return 0;
    return claim?.procedures.reduce((acc, procedure) => {
      return procedure.duration + acc;
    }, 0);
  }, [claim]);

  return (
    <Box py={2}>
      {(!claim?.payor_claim_id ||
        !claim?.clearing_house_claim_id ||
        (claim?.secondary_status && !claim?.secondary_payor_claim_id)) && (
        <Box sx={{ display: 'flex' }} my={2}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
          <Typography
            data-testid={'coNoPayorClaimIdError'}
            textAlign={'left'}
            alignSelf="start"
            lineHeight={'18px'}
            ml={0.5}
            color={FORTA_ERROR}
          >
            {getPayorClaimIdErrorMessage(
              claim?.payor_claim_id ?? null,
              claim?.clearing_house_claim_id ?? null,
              claim?.secondary_payor_claim_id ?? null,
              claim?.secondary_status ?? null,
            )}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography fontSize={14} display="inline">
          Please ensure all updated information needed for claim resubmission has been added.{' '}
        </Typography>
        <Typography display="inline" fontWeight={700} color={FORTA_BLUE.primary} fontSize={14}>
          A new claim ID will be generated.{' '}
        </Typography>
        <Typography display="inline" fontSize={14}>
          Please review sessions included are accurate for this claim, including the date range and
          the BCBA name listed. Once a claim is built, it cannot be deleted.
        </Typography>
      </Box>
      <Box mt={2} mb={3} fontSize={14}>
        <Grid container spacing={1}>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Client
            </Typography>
            <Typography
              fontSize={14}
              fontWeight={500}
            >{`${claim?.client?.first_name} ${claim?.client?.last_name}`}</Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              BCBA
            </Typography>
            <Typography
              fontSize={14}
              fontWeight={500}
            >{`${claim?.bcba?.first_name} ${claim?.bcba?.last_name}`}</Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Submitter
            </Typography>
            <Typography
              fontSize={14}
              fontWeight={500}
            >{`${claim?.biller?.first_name} ${claim?.biller?.last_name}`}</Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Clearinghouse claim ID
            </Typography>
            <Typography fontSize={14} fontWeight={500} sx={{ wordWrap: 'break-word' }}>
              {claim?.clearing_house_claim_id}
            </Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Total sessions
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              {claim?.procedures?.length}
            </Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Total duration
            </Typography>
            <Typography fontSize={14} fontWeight={500}>
              {formatSecondsToHHMM(totalDuration)}
            </Typography>
          </Grid>
          <Grid item xs={3} mt={2}>
            <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
              Payor claim ID
            </Typography>
            <Typography fontSize={14} fontWeight={500} sx={{ wordWrap: 'break-word' }}>
              {claim?.payor_claim_id}
            </Typography>
          </Grid>
          {claim?.secondary_payor_claim_id && (
            <Grid item xs={3} mt={2}>
              <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
                Secondary payor claim ID
              </Typography>
              <Typography fontSize={14} fontWeight={500} sx={{ wordWrap: 'break-word' }}>
                {claim?.secondary_payor_claim_id}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
      <ThemeProvider theme={correctClaimTableTheme}>
        <FHMUIDataTable
          containerStyles={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '570px',
            backgroundColor: FORTA_WHITE,
            backgroundSize: '100%',
            boxShadow: 'none',
            borderRadius: '10px',
            margin: 0,
            overflowY: 'auto',
          }}
          title=""
          data={getSessionsInClaimsData(claim)}
          columns={claimProcedureTableColDefs}
          options={sessionsInClaimTableOptions}
        />
      </ThemeProvider>
    </Box>
  );
}

function CorrectClaimDialog({ show, onClose }: ICorrectClaimDialogProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: claim, loading: claimsLoading } = useSelector(
    (state: RootState) => state.claims,
  );

  const handleCorrectClaim = async () => {
    if (!claim) return;
    const data = {
      date_generated: moment(new Date()).toISOString(),
      utc_offset_date_generated: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      claimsActions.resubmitClaim({ getAccessTokenSilently, data, claimId: claim.id }),
    );
    if (response?.payload?.id) {
      history.navigate(`/billings/claims/${response.payload.id}`);
      onClose();
    }
  };

  return (
    <FHConfirmDialog
      data-testid="coCorrectClaimModal"
      cancelBtnTestId="coCorrectClaimModalCancelBtn"
      submitBtnTestId="coCorrectClaimModalSubmitBtn"
      open={show}
      title="Correct claim"
      canceltext="Cancel"
      submittext="Correct claim"
      dialogStyles={{
        padding: '16px',
        maxHeight: '85vh',
        height: 'auto',
        width: '800px',
        maxWidth: '800px',
      }}
      sx={{
        '& .MuiModal-backdrop': {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
      }}
      titleStyles={{ color: FORTA_BLUE.primary }}
      disableSubmitButton={
        !claim?.payor_claim_id ||
        !claim?.clearing_house_claim_id ||
        Boolean(claim?.secondary_status && !claim?.secondary_payor_claim_id) ||
        claimsLoading
      }
      footerContainerStyles={{ mt: 2 }}
      onClose={onClose}
      onSubmit={handleCorrectClaim}
      contentRenderer={() => <CorrectClaimContent claim={claim} />}
    />
  );
}

export default CorrectClaimDialog;
